// Files the backend serves from its Report directory (run workbooks, content briefs, checklist and task
// exports, admin spreadsheets) and the invoice PDFs. They used to be opened as bare links; the routes now
// require the session (or the report's share key), so every download is an authenticated fetch whose
// blob is saved from the browser.

// The store is loaded when a download starts, never imported at the top of this file. components/mixin.js
// imports this helper, and a static store import here closes a loop (store -> router -> route components
// -> Menu.vue -> mixin.js) that crashes the built app at load with "Cannot access 'mixin' before
// initialization". Keep this module free of static imports of the store, the router and the API.
function loadStore() {
  return import('@/store').then((module) => module.default)
}

const MIME_BY_EXT = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pdf: 'application/pdf',
  csv: 'text/csv',
  txt: 'text/plain',
}

export function fileNameOf(path) {
  return String(path || '').split(/[\\/]/).pop()
}

export function saveBlobAs(data, fileName) {
  const name = fileNameOf(fileName)
  const ext = name.split('.').pop().toLowerCase()
  const blob = new Blob([data], { type: MIME_BY_EXT[ext] || 'application/octet-stream' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  setTimeout(() => {
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }, 100)
}

/** A file of the Report directory, by name. `secretKey` is the report share key for a shared report page. */
export function downloadServedFile(fileName, { secretKey = '' } = {}) {
  const name = fileNameOf(fileName)
  if (!name) return Promise.resolve()
  return loadStore()
    .then((store) => store.dispatch('downloadExcelReport', { filePath: name, secretKey }))
    .then((response) => saveBlobAs(response.data, name))
}

/** A page run's workbook: the S3 copy when the run has one, otherwise the served file. */
export function downloadPageRunReport(pageRun, { secretKey = '' } = {}) {
  if (!pageRun) return Promise.resolve()
  if (pageRun.reportResourceUrl) {
    window.open(pageRun.reportResourceUrl, '_blank')
    return Promise.resolve()
  }
  return downloadServedFile(pageRun.excelFile, { secretKey: secretKey || pageRun.secretKey || '' })
}

/** An invoice PDF generated by the invoice export routes. */
export function downloadInvoicePdf(fileName) {
  const name = fileNameOf(fileName)
  if (!name) return Promise.resolve()
  return loadStore()
    .then((store) => store.dispatch('downloadInvoiceFile', { fileName: name }))
    .then((response) => saveBlobAs(response.data, name))
}
